/*
 * @Description: 个人轨迹回放 — 地图（参考 aiWeb TrackMap 的回放实现）
 *   节点 marker（起/停/拍照/当前）+ 轨迹线渐进显示（线随 playTime「长」出来）
 *   + 播放头 + 播放时地图平滑跟随（低通滤波）+ 节点详情卡（上一个/下一个）。
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import AMapLoader from '@amap/amap-jsapi-loader'
import dayjs from 'dayjs'
import CONFIG from '../../../../../constants/config'
import { CockpitMapStyle, MAP_FEATURES } from '../../../constants'
import { applyMapStyle } from '../../../utils/mapStyle'
import {
  TimelineData,
  TimelineEvent,
  parseLngLat
} from '../../../types/cockpit.types'
import { PoiItem } from '../../../types/poi.types'
import { buildPoiMarkerEl } from '../../../utils/poiMarker'
import { FlatPoint, positionAt } from '../../utils/playback'
import './index.scss'

export interface ReplayMapRef {
  /** 定位到指定节点（供右侧列表联动） */
  focusNode: (nodeIndex: number) => void
  /** 居中并放大到指定坐标（点位定位用） */
  centerTo: (lng: number, lat: number) => void
}

interface ReplayMapProps {
  timeline: TimelineData | null
  /** 当前回放时刻（unix 毫秒），驱动轨迹渐进显示 + 播放头 */
  playTime: number
  /** 是否正在播放（决定地图是否平滑跟随播放头） */
  playing: boolean
  /** 已保存的点位（E1），常驻展示在地图上 */
  pois: PoiItem[]
  /** 地图样式（纯净 / 普通 / 卫星） */
  mapStyle: CockpitMapStyle
}

/** 可作为「节点」展示的事件类型（move 不算节点） */
type NodeEvent = Extract<
  TimelineEvent,
  { type: 'start' | 'stay' | 'photo' | 'current' }
>

interface SolidSegment {
  polyline: any
  /** 段内全路径坐标（按 locateTime 升序） */
  path: Array<[number, number]>
  /** 与 path 对应的 locateTime（ms），缺时为 0 */
  times: number[]
  /** 段内有效时间窗（正时间的 min / max） */
  minTime: number
  maxTime: number
  /** 累计距离表（无有效时间窗时按距离兜底推进） */
  dist: { cumulative: number[]; total: number }
}

interface DashedSegment {
  polyline: any
  /** 中断段折线点（纯数字，setPath 时拷贝新数组，避免被高德原地改写） */
  path: Array<[number, number]>
  startTime: number
  endTime: number
}

const CONTAINER_ID = 'replay-map-container'

/** 节点 marker 的 HTML（起/停/拍照/当前；文案静态，安全） */
function buildNodeMarkerHtml(node: NodeEvent, selected: boolean): string {
  const selectedClass = selected ? 'is-selected' : ''
  let kindClass = 'is-current'
  // 结尾「当前位置」节点：当天显示「现」，历史日期显示「终」
  let inner = dayjs(node.time).isSame(dayjs(), 'day') ? '现' : '终'
  if (node.type === 'start') {
    kindClass = 'is-start'
    inner = '起'
  } else if (node.type === 'stay') {
    kindClass = 'is-stay'
    inner = '停'
  } else if (node.type === 'photo') {
    kindClass = 'is-photo'
    inner = '📷'
  }
  return `<div class="replay-node-marker ${kindClass} ${selectedClass}">${inner}</div>`
}

/** 轨迹点坐标解析容错：兼容 location:"lng,lat" 字符串与数值 lng/lat（对齐 aiWeb getLngLat） */
function pointLngLat(p: {
  location?: string
  lng?: number
  lat?: number
  longitude?: number
  latitude?: number
}): [number, number] | null {
  if (p.location) {
    const ll = parseLngLat(p.location)
    if (ll) return [ll.lng, ll.lat]
  }
  const lng = Number(p.lng ?? p.longitude)
  const lat = Number(p.lat ?? p.latitude)
  if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat]
  return null
}

/** 取事件的进入/离开坐标（用于连接中断虚线） */
function eventEntryExit(
  e: TimelineEvent
): { entry: [number, number]; exit: [number, number] } | null {
  if (e.type === 'move') {
    if (!e.points || e.points.length === 0) return null
    const first = pointLngLat(e.points[0])
    const last = pointLngLat(e.points[e.points.length - 1])
    if (!first || !last) return null
    return { entry: first, exit: last }
  }
  const coord: [number, number] = [e.lng, e.lat]
  return { entry: coord, exit: coord }
}

/** 累计距离表（无有效时间窗时按距离均匀推进，对齐 aiWeb buildPathDistanceTable） */
function buildDistTable(path: Array<[number, number]>): {
  cumulative: number[]
  total: number
} {
  const cumulative = [0]
  let total = 0
  for (let i = 1; i < path.length; i++) {
    const dx = path[i][0] - path[i - 1][0]
    const dy = path[i][1] - path[i - 1][1]
    total += Math.sqrt(dx * dx + dy * dy)
    cumulative.push(total)
  }
  return { cumulative, total }
}

/**
 * 取段在当前时刻已「长」出的子路径（对齐 aiWeb getAnimatedPath）。
 * 优先级：locateTime 时间表 → 累计距离表 → 等分下标，三级兜底保证一定能画。
 *
 * @param path 段内全路径（按时间升序）
 * @param times 与 path 对应的 locateTime，缺时为 0（时间分支会跳过）
 * @param hasTimeWindow 段是否有有效时间窗（maxTime > minTime）
 * @param dist 累计距离表
 * @param currentTime 当前回放时刻（ms），时间分支用
 * @param fallbackProgress 无时间窗时的全局进度（0~1），距离/等分分支用
 */
function getAnimatedPath(
  path: Array<[number, number]>,
  times: number[],
  hasTimeWindow: boolean,
  dist: { cumulative: number[]; total: number },
  currentTime: number,
  fallbackProgress: number
): Array<[number, number]> {
  if (path.length <= 1) return path.map((p) => [p[0], p[1]])

  // 1. 时间表推进：画到「该时刻已走到的位置」，停留段进度走但线不动
  if (hasTimeWindow) {
    let lastIndex = -1
    let nextValidIndex = -1
    for (let i = 0; i < times.length; i++) {
      const t = times[i]
      if (t <= 0) continue // 跳过缺时点，避免末尾 0 时间点把线瞬间拉到终点
      if (t <= currentTime) lastIndex = i
      else {
        nextValidIndex = i
        break
      }
    }
    if (lastIndex < 0) return [[path[0][0], path[0][1]]]
    const out: Array<[number, number]> = path
      .slice(0, lastIndex + 1)
      .map((q) => [q[0], q[1]])
    if (nextValidIndex > lastIndex) {
      const tc = times[lastIndex]
      const tn = times[nextValidIndex]
      if (tn > tc) {
        const r = (currentTime - tc) / (tn - tc)
        const c = path[lastIndex]
        const n = path[nextValidIndex]
        out.push([c[0] + (n[0] - c[0]) * r, c[1] + (n[1] - c[1]) * r])
      }
    }
    return out
  }

  const p = Math.max(0, Math.min(1, fallbackProgress))

  // 2. 距离表推进
  if (dist.total > 0) {
    const target = p * dist.total
    let nextIndex = dist.cumulative.length - 1
    for (let i = 0; i < dist.cumulative.length; i++) {
      if (dist.cumulative[i] >= target) {
        nextIndex = i
        break
      }
    }
    if (nextIndex === 0) return [[path[0][0], path[0][1]]]
    const completed = nextIndex - 1
    const segDist = dist.cumulative[nextIndex] - dist.cumulative[completed]
    const r = segDist > 0 ? (target - dist.cumulative[completed]) / segDist : 0
    const out: Array<[number, number]> = path
      .slice(0, completed + 1)
      .map((q) => [q[0], q[1]])
    const c = path[completed]
    const n = path[nextIndex]
    out.push([c[0] + (n[0] - c[0]) * r, c[1] + (n[1] - c[1]) * r])
    return out
  }

  // 3. 等分下标兜底
  const sp = p * (path.length - 1)
  const completed = Math.min(Math.floor(sp), path.length - 1)
  const r = sp - completed
  const out: Array<[number, number]> = path
    .slice(0, completed + 1)
    .map((q) => [q[0], q[1]])
  const nextIdx = Math.min(completed + 1, path.length - 1)
  if (nextIdx > completed) {
    const c = path[completed]
    const n = path[nextIdx]
    out.push([c[0] + (n[0] - c[0]) * r, c[1] + (n[1] - c[1]) * r])
  }
  return out
}

/**
 * 沿折线按 0~1 进度截取前半段（对齐 aiWeb slicePathByProgress）。
 * 中断（虚线）段顺着折线「长」出来用；按累计长度插值，返回全新数组
 * （高德 setPath 会原地改写传入引用，必须每次构造新数组）。
 */
function slicePathByProgress(
  path: Array<[number, number]>,
  frac: number
): Array<[number, number]> {
  if (path.length < 2) return path.map((p) => [p[0], p[1]])
  const f = Math.max(0, Math.min(1, Number(frac) || 0))
  if (f <= 0) return [[path[0][0], path[0][1]]]
  if (f >= 1) return path.map((p) => [p[0], p[1]])
  const segLens: number[] = []
  let total = 0
  for (let i = 1; i < path.length; i++) {
    const dx = path[i][0] - path[i - 1][0]
    const dy = path[i][1] - path[i - 1][1]
    const len = Math.sqrt(dx * dx + dy * dy)
    segLens.push(len)
    total += len
  }
  if (total <= 0) return [[path[0][0], path[0][1]]]
  const target = total * f
  const out: Array<[number, number]> = [[path[0][0], path[0][1]]]
  let acc = 0
  for (let i = 1; i < path.length; i++) {
    const len = segLens[i - 1]
    if (acc + len < target) {
      out.push([path[i][0], path[i][1]])
      acc += len
      continue
    }
    const t = len > 0 ? (target - acc) / len : 0
    out.push([
      path[i - 1][0] + (path[i][0] - path[i - 1][0]) * t,
      path[i - 1][1] + (path[i][1] - path[i - 1][1]) * t
    ])
    break
  }
  return out
}

const ReplayMap = forwardRef<ReplayMapRef, ReplayMapProps>(
  ({ timeline, playTime, playing, pois, mapStyle }, ref) => {
    const mapRef = useRef<any>(null)
    // 卫星图层（切到卫星样式时叠加）
    const satelliteRef = useRef<any>(null)
    // 地图点位 marker
    const poiMarkersRef = useRef<any[]>([])
    const nodeMarkersRef = useRef<Array<{ marker: any; time: number }>>([])
    const solidSegsRef = useRef<SolidSegment[]>([])
    const dashedSegsRef = useRef<DashedSegment[]>([])
    const flatRef = useRef<FlatPoint[]>([])
    // 全局时间窗（所有实线点的 min / max locateTime），无时间窗分段的兜底进度用
    const globalMinRef = useRef(0)
    const globalMaxRef = useRef(0)
    const infoWindowRef = useRef<any>(null)
    // 平滑跟随的滤波中心
    const smoothCenterRef = useRef<{ lng: number; lat: number } | null>(null)
    const mapReadyRef = useRef(false)
    // 地图就绪用 state 驱动：timeline 可能先于地图 ready 到达，ready 后须用最新数据补建一次
    const [mapReady, setMapReady] = useState(false)

    const [selectedNodeIndex, setSelectedNodeIndex] = useState(-1)
    const selectedNodeIndexRef = useRef(-1)
    selectedNodeIndexRef.current = selectedNodeIndex
    const playingRef = useRef(playing)
    playingRef.current = playing
    const playTimeRef = useRef(playTime)
    playTimeRef.current = playTime
    // 上一次的回放时刻（判断「回退」：playTime 减小时取消被越过的选中节点）
    const prevPlayTimeRef = useRef(playTime)
    // 当前选中是否由播放自动弹出（区别于手动点击，便于自动收起）
    const autoSelectedRef = useRef(false)

    // 节点事件（起/停/拍照/当前），按出现顺序；memo 化避免每帧重建
    const nodes = useMemo<NodeEvent[]>(
      () =>
        (timeline?.events ?? []).filter(
          (e): e is NodeEvent => e.type !== 'move'
        ),
      [timeline]
    )
    const nodesRef = useRef<NodeEvent[]>(nodes)
    nodesRef.current = nodes

    /** 清除轨迹线 */
    const clearSegments = useCallback(() => {
      if (mapRef.current) {
        const all = [
          ...solidSegsRef.current.map((s) => s.polyline),
          ...dashedSegsRef.current.map((s) => s.polyline)
        ]
        if (all.length) mapRef.current.remove(all)
      }
      solidSegsRef.current = []
      dashedSegsRef.current = []
    }, [])

    /** 清除节点 marker */
    const clearNodeMarkers = useCallback(() => {
      if (mapRef.current && nodeMarkersRef.current.length) {
        mapRef.current.remove(nodeMarkersRef.current.map((n) => n.marker))
      }
      nodeMarkersRef.current = []
    }, [])

    /** 清除点位 marker */
    const clearPois = useCallback(() => {
      if (mapRef.current && poiMarkersRef.current.length) {
        mapRef.current.remove(poiMarkersRef.current)
      }
      poiMarkersRef.current = []
    }, [])

    /** 渲染已保存的点位（常驻），点击居中放大到该点位 */
    const renderPois = useCallback(() => {
      if (!mapRef.current || !window.AMap) return
      clearPois()
      pois.forEach((poi) => {
        const marker = new window.AMap.Marker({
          position: [poi.lng, poi.lat],
          // 高德 Marker 运行时支持 HTMLElement content，类型声明仅标 string
          content: buildPoiMarkerEl(poi) as unknown as string,
          // 底部倒三角尖对准坐标
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(0, 0),
          zIndex: 60
        })
        marker.on('click', () => {
          if (mapRef.current) {
            mapRef.current.setZoomAndCenter(16, [poi.lng, poi.lat])
          }
        })
        mapRef.current.add(marker)
        poiMarkersRef.current.push(marker)
      })
    }, [pois, clearPois])

    /** 构建轨迹线（实线段含点数据用于渐进显示；中断段连接相邻节点）+ 扁平点列 + 自适应 */
    const buildSegments = useCallback(() => {
      if (!mapRef.current || !window.AMap || !timeline) return
      clearSegments()
      const events = timeline.events
      const flat: FlatPoint[] = []

      const isNode = (ev: TimelineEvent): ev is NodeEvent => ev.type !== 'move'

      /** 建一条红虚线段（中断 / 点到点），按 startTime→endTime 渐进生长 */
      const pushDashed = (
        from: [number, number],
        to: [number, number],
        startTime: number,
        endTime: number
      ) => {
        // 连续且端点完全相同的中断段合并成一段：本数据里一串无几何的 dashed move
        // （离线/没定位等不同原因）往前/往后找到的是同一对节点，会画出多条「新乐居→上寮」同线段，
        // 各自从头生长会让跟随镜头「前进→回退→前进」来回跳，合并成一段连续生长即可平滑。
        const samePoint = (a: [number, number], b: [number, number]) =>
          Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9
        const segs = dashedSegsRef.current
        const last = segs.length ? segs[segs.length - 1] : null
        if (
          last &&
          samePoint(last.path[0], from) &&
          samePoint(last.path[last.path.length - 1], to)
        ) {
          last.startTime = Math.min(last.startTime, startTime)
          last.endTime = Math.max(last.endTime, endTime)
          return
        }
        // 纯数字折线点，setPath 时拷贝新数组（高德会原地把元素替换成 LngLat 对象）
        const dashedPath: Array<[number, number]> = [
          [from[0], from[1]],
          [to[0], to[1]]
        ]
        const polyline = new window.AMap.Polyline({
          path: dashedPath.map((p) => [p[0], p[1]]),
          strokeColor: '#EA4D3D',
          strokeWeight: 4,
          strokeOpacity: 0.95,
          strokeStyle: 'dashed',
          lineJoin: 'round',
          lineCap: 'round',
          zIndex: 60
        })
        ;(polyline as any).hide()
        mapRef.current.add(polyline)
        dashedSegsRef.current.push({
          polyline,
          path: dashedPath,
          startTime,
          endTime
        })
      }

      events.forEach((e, i) => {
        if (e.type !== 'move') return

        // 中断段：红虚线连「前一个有几何 event 的离开点 → 后一个有几何 event 的进入点」
        if (e.lineType === 'dashed') {
          let prev: [number, number] | null = null
          for (let j = i - 1; j >= 0; j--) {
            const ee = eventEntryExit(events[j])
            if (ee) {
              prev = ee.exit
              break
            }
          }
          let next: [number, number] | null = null
          for (let j = i + 1; j < events.length; j++) {
            const ee = eventEntryExit(events[j])
            if (ee) {
              next = ee.entry
              break
            }
          }
          if (!prev || !next) return
          pushDashed(prev, next, e.startTime, e.endTime)
          return
        }

        // 实线段：蓝线连首/中/尾点（坐标容错），按 locateTime 升序（真实 GPS 点段内可能乱序）
        const collected: Array<{ lng: number; lat: number; time: number }> = []
        ;(e.points ?? []).forEach((p) => {
          const ll = pointLngLat(p)
          if (ll) {
            collected.push({
              lng: ll[0],
              lat: ll[1],
              time: Number(p.locateTime) || 0
            })
          }
        })
        if (!collected.length) return
        // 把紧邻的前后节点纳入蓝线端点：solid move 和前后点之间只用蓝线，不画红虚线
        const prevE = events[i - 1]
        if (prevE && isNode(prevE)) {
          collected.push({ lng: prevE.lng, lat: prevE.lat, time: prevE.time })
        }
        const nextE = events[i + 1]
        if (nextE && isNode(nextE)) {
          collected.push({ lng: nextE.lng, lat: nextE.lat, time: nextE.time })
        }
        collected.sort((a, b) => a.time - b.time)
        if (collected.length < 2) return

        const path: Array<[number, number]> = collected.map((c) => [
          c.lng,
          c.lat
        ])
        const times = collected.map((c) => c.time)
        collected.forEach((c) =>
          flat.push({ lng: c.lng, lat: c.lat, time: c.time })
        )
        // 段内有效时间窗（只取正时间）
        let minTime = Infinity
        let maxTime = -Infinity
        times.forEach((t) => {
          if (t > 0) {
            if (t < minTime) minTime = t
            if (t > maxTime) maxTime = t
          }
        })
        if (!Number.isFinite(minTime)) minTime = 0
        if (!Number.isFinite(maxTime)) maxTime = 0

        const polyline = new window.AMap.Polyline({
          // 初始用全路径，便于 setFitView 取全貌；随后 applyProgress 立即切片
          path,
          strokeColor: '#1677ff',
          strokeWeight: 4,
          strokeOpacity: 0.9,
          showDir: true,
          lineJoin: 'round',
          lineCap: 'round',
          zIndex: 60
        })
        mapRef.current.add(polyline)
        solidSegsRef.current.push({
          polyline,
          path,
          times,
          minTime,
          maxTime,
          dist: buildDistTable(path)
        })
      })

      // 其余点到点：时间轴上相邻两个节点之间没有任何 move 段 → 红虚线连接。
      // 含 move 的相邻对不画（solid 衔接处只走蓝线；两端都是 solid move 的点两端都不画）
      for (let i = 0; i < events.length - 1; i++) {
        const cur = events[i]
        const nxt = events[i + 1]
        if (!isNode(cur) || !isNode(nxt)) continue
        pushDashed([cur.lng, cur.lat], [nxt.lng, nxt.lat], cur.time, nxt.time)
      }

      // 把所有节点（起/停/拍照/当前）并入 flat：positionAt 是「无可见绘制段时」的兜底，
      // 必须覆盖全程。否则起点等不与实线相邻的节点不在 flat 里，t 落在首段虚线之前时
      // 兜底会取到错误的 flat[0]（首个实线点，在很远的另一地点），导致播放头来回跳。
      events.forEach((e) => {
        if (isNode(e)) flat.push({ lng: e.lng, lat: e.lat, time: e.time })
      })

      flat.sort((a, b) => a.time - b.time)
      flatRef.current = flat
      globalMinRef.current = flat.length ? flat[0].time : 0
      globalMaxRef.current = flat.length ? flat[flat.length - 1].time : 0

      // 自适应到完整轨迹定缩放（此时实线为全路径，随后 applyProgress 会切片并把起点居中）。
      // 用 immediately=true：动画版会在 applyProgress 居中起点之后才完成、把镜头又拉回全轨中心，
      // 导致进入回放页时起点没落在屏幕中心。
      const lines = solidSegsRef.current.map((s) => s.polyline)
      if (lines.length) {
        mapRef.current.setFitView(lines, true, [80, 80, 80, 120])
      }
    }, [timeline, clearSegments])

    /** 渲染节点 marker（含选中高亮） */
    const renderNodes = useCallback(() => {
      if (!mapRef.current || !window.AMap) return
      clearNodeMarkers()
      nodes.forEach((node, idx) => {
        const marker = new window.AMap.Marker({
          position: [node.lng, node.lat],
          content: buildNodeMarkerHtml(
            node,
            idx === selectedNodeIndexRef.current
          ),
          anchor: 'center',
          zIndex: 100 + idx
        })
        // 渐显：播放未到达该节点且未选中时先隐藏
        if (
          playTimeRef.current < node.time &&
          idx !== selectedNodeIndexRef.current
        ) {
          ;(marker as any).hide()
        }
        marker.on('click', () => {
          autoSelectedRef.current = false
          setSelectedNodeIndex(idx)
        })
        mapRef.current.add(marker)
        nodeMarkersRef.current.push({ marker, time: node.time })
      })
    }, [nodes, clearNodeMarkers])

    /**
     * 把播放头（轨迹绘制到的位置）平移到屏幕中心，不改变缩放。
     * 只在 applyProgress（playTime 变化，即播放 / 拖动进度条）时调用，
     * 故暂停查看某节点时不会被它覆盖——这里无需再判断选中节点。
     */
    const followHead = useCallback((pos: { lng: number; lat: number }) => {
      if (!mapRef.current) return
      mapRef.current.setCenter([pos.lng, pos.lat])
    }, [])

    /** 按当前回放时刻：渐进显示轨迹线 + 移动播放头 + 平滑跟随 */
    const applyProgress = useCallback(
      (t: number) => {
        if (!mapReadyRef.current || !mapRef.current || !window.AMap) return

        // 实线渐进：每段按自己的时间窗推进（对齐 aiWeb），无时间窗段退化到全局进度
        const gMin = globalMinRef.current
        const gMax = globalMaxRef.current
        const fallbackProgress =
          gMax > gMin ? Math.max(0, Math.min(1, (t - gMin) / (gMax - gMin))) : 0
        // 轨迹当前绘制到的位置（播放头）= 最后一个可见实线段的末点，随画线平滑移动
        let drawHead: [number, number] | null = null
        solidSegsRef.current.forEach((seg) => {
          const hasTimeWindow = seg.maxTime > seg.minTime
          // 单点/同刻实线段（无时间窗但有有效时刻，如孤立 GPS 点夹在两段虚线之间）：
          // 到点才整段显示。否则 getAnimatedPath 对无时间窗段按「全局进度」绘制，一开播
          // 就画出一截、把播放头拽到那个孤点上（表现为镜头先跑到没点位的地方再跳回来）。
          if (!hasTimeWindow && seg.minTime > 0) {
            if (t >= seg.minTime) {
              seg.polyline.setPath(seg.path.map((p) => [p[0], p[1]]))
              seg.polyline.show()
              drawHead = seg.path[seg.path.length - 1]
            } else {
              seg.polyline.hide()
            }
            return
          }
          const animated = getAnimatedPath(
            seg.path,
            seg.times,
            hasTimeWindow,
            seg.dist,
            t,
            fallbackProgress
          )
          if (animated.length >= 2) {
            seg.polyline.setPath(animated)
            seg.polyline.show()
            // 时间升序：最后一个可见段（当前正在生长的段）的末点即绘制头
            drawHead = animated[animated.length - 1]
          } else {
            seg.polyline.hide()
          }
        })
        // 中断段：按 startTime→endTime 沿折线渐进生长（对齐 aiWeb）
        dashedSegsRef.current.forEach((seg) => {
          const pts = seg.path
          if (pts.length < 2) return
          const startTime = seg.startTime
          const endTime = seg.endTime || startTime
          const head: [number, number] = [pts[0][0], pts[0][1]]
          // 还没开始：缩到起点 + 隐藏
          if (t <= startTime) {
            seg.polyline.setPath([head])
            seg.polyline.hide()
            return
          }
          // 已走完（或无有效时长）：整段显示
          if (t >= endTime || endTime <= startTime) {
            seg.polyline.setPath(pts.map((p) => [p[0], p[1]]))
            seg.polyline.show()
            return
          }
          // 进行中：按时间比例沿折线长出
          const localProgress = Math.max(
            0,
            Math.min(1, (t - startTime) / (endTime - startTime))
          )
          // 太短（<2%）几乎是个点，高德渲染易抖动，先藏
          if (localProgress < 0.02) {
            seg.polyline.setPath([head])
            seg.polyline.hide()
            return
          }
          const sliced = slicePathByProgress(pts, localProgress)
          seg.polyline.setPath(sliced)
          seg.polyline.show()
          // 中断段正在生长（t 落在其时段内，即当前播放头所在段）→ 镜头也沿虚线跟随其末点，
          // 覆盖实线段那个冻结在断点的播放头，避免续接时镜头跳跃
          drawHead = sliced[sliced.length - 1]
        })

        // 节点 marker 渐显：到达时刻才出现（选中的始终显示）
        nodeMarkersRef.current.forEach((nm, idx) => {
          if (t >= nm.time || idx === selectedNodeIndexRef.current) {
            nm.marker.show()
          } else {
            nm.marker.hide()
          }
        })

        // 跟随：优先用画线实际绘制到的末点（随画线平滑移动）；无可见实线时兜底 positionAt
        const head = drawHead
          ? { lng: drawHead[0], lat: drawHead[1] }
          : positionAt(flatRef.current, t)
        if (head) followHead(head)
      },
      [followHead]
    )

    // 初始化地图（仅一次）
    useEffect(() => {
      let destroyed = false
      AMapLoader.load({
        key: CONFIG.GAODE_KEY,
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.ToolBar']
      })
        .then((AMap) => {
          if (destroyed) return
          window.AMap = AMap
          const map = new AMap.Map(CONTAINER_ID, {
            zoom: 13,
            mapStyle: 'amap://styles/whitesmoke',
            viewMode: '2D',
            features: MAP_FEATURES
          })
          map.addControl(
            // 缩放控件放到地图左侧（右侧被成员轨迹栏遮挡）；抬高避开底部浮条
            new AMap.ToolBar({ position: { bottom: '90px', left: '24px' } })
          )
          map.on('click', () => setSelectedNodeIndex(-1))
          mapRef.current = map
          mapReadyRef.current = true
          // 触发重渲染，让下方 timeline effect 用「当前最新」的 timeline 补建，
          // 避免 timeline 先于地图 ready 到达时这里拿到旧闭包（空 timeline）只建一次空图
          setMapReady(true)
        })
        .catch((error) => {
          console.error('轨迹回放地图初始化失败:', error)
        })

      return () => {
        destroyed = true
        clearSegments()
        clearNodeMarkers()
        clearPois()
        if (infoWindowRef.current) {
          infoWindowRef.current.close()
          infoWindowRef.current = null
        }
        if (mapRef.current) {
          mapRef.current.destroy()
          mapRef.current = null
        }
        mapReadyRef.current = false
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // timeline 变化、或地图刚就绪 → 用最新数据重建轨迹线 + 节点，清空选中，刷新进度
    useEffect(() => {
      if (!mapReady) return
      setSelectedNodeIndex(-1)
      smoothCenterRef.current = null
      buildSegments()
      renderNodes()
      applyProgress(playTime)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeline, mapReady])

    // 回放时刻变化 → 渐进显示 + 播放头 + 跟随
    useEffect(() => {
      applyProgress(playTime)
    }, [playTime, applyProgress])

    // 地图样式切换（纯净 / 普通 / 卫星）
    useEffect(() => {
      if (!mapReady || !mapRef.current || !window.AMap) return
      applyMapStyle(mapRef.current, window.AMap, mapStyle, satelliteRef)
    }, [mapStyle, mapReady])

    // 点位变化、或地图刚就绪 → 重绘点位 marker
    useEffect(() => {
      if (mapReady) renderPois()
    }, [renderPois, mapReady])

    // 播放经过节点 → 自动弹出该节点 callout；离开后自动收起。
    // 拖动回退（playTime 减小）越过选中节点时，取消选中（自动 / 手动点选都消失）。
    useEffect(() => {
      const prev = prevPlayTimeRef.current
      prevPlayTimeRef.current = playTime
      if (!mapReadyRef.current) return

      // 回退越过选中节点 → 取消（仅在 playTime 实际减小时，避免误删刚点选的未来节点）
      if (playTime < prev && selectedNodeIndexRef.current >= 0) {
        const selTime = nodes[selectedNodeIndexRef.current]?.time
        if (selTime != null && playTime < selTime) {
          autoSelectedRef.current = false
          setSelectedNodeIndex(-1)
          return
        }
      }

      // 仅播放中做「经过即自动选中、离开即自动收起」
      if (!playing) return
      const atIdx = nodes.findIndex((n) => Math.abs(n.time - playTime) < 800)
      if (atIdx >= 0) {
        if (selectedNodeIndexRef.current !== atIdx) {
          autoSelectedRef.current = true
          setSelectedNodeIndex(atIdx)
        }
      } else if (autoSelectedRef.current && selectedNodeIndexRef.current >= 0) {
        autoSelectedRef.current = false
        setSelectedNodeIndex(-1)
      }
    }, [playTime, playing, nodes])

    // 选中节点变化 → 重绘 marker 高亮 + 弹/关详情卡
    useEffect(() => {
      if (!mapReadyRef.current || !mapRef.current || !window.AMap) return
      renderNodes()

      const node = selectedNodeIndex >= 0 ? nodes[selectedNodeIndex] : undefined
      if (!node) {
        if (infoWindowRef.current) infoWindowRef.current.close()
        return
      }

      const card = buildNodeCard(node, selectedNodeIndex, nodes.length)
      card.addEventListener('click', (ev) => {
        ev.stopPropagation()
        const target = (ev.target as HTMLElement).closest('[data-action]')
        if (!target) return
        const action = target.getAttribute('data-action')
        const total = nodesRef.current.length
        if (action === 'close') {
          setSelectedNodeIndex(-1)
        } else if (action === 'prev') {
          setSelectedNodeIndex((i) => (i > 0 ? i - 1 : i))
        } else if (action === 'next') {
          setSelectedNodeIndex((i) => (i < total - 1 ? i + 1 : i))
        }
      })

      if (!infoWindowRef.current) {
        // 高德 InfoWindow 运行时接受配置项，类型声明未带构造参数
        infoWindowRef.current = new (window.AMap.InfoWindow as any)({
          isCustom: true,
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(0, -28),
          // 不让信息窗自动移动地图——播放中跟随播放头由 followHead 负责，避免抢镜
          autoMove: false
        })
      }
      infoWindowRef.current.setContent(card)
      infoWindowRef.current.open(mapRef.current, [node.lng, node.lat])
      // 仅「手动点选节点」才居中到该节点；播放中经过节点的自动选中不抢镜，
      // 交给 followHead 持续跟随播放头（否则相机被拉到滞后的节点上，轨迹头就偏离中心）
      if (!autoSelectedRef.current) {
        mapRef.current.setCenter([node.lng, node.lat])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodeIndex, nodes])

    useImperativeHandle(
      ref,
      () => ({
        focusNode: (nodeIndex: number) => {
          autoSelectedRef.current = false
          setSelectedNodeIndex(nodeIndex)
        },
        centerTo: (lng: number, lat: number) => {
          if (mapRef.current) mapRef.current.setZoomAndCenter(16, [lng, lat])
        }
      }),
      []
    )

    return (
      <div className="replay-map">
        <div id={CONTAINER_ID} className="replay-map__canvas" />
      </div>
    )
  }
)

ReplayMap.displayName = 'ReplayMap'

/** 用 DOM 构建节点详情卡（textContent，避免后端字段经 innerHTML 引入 XSS） */
function buildNodeCard(
  node: NodeEvent,
  index: number,
  total: number
): HTMLDivElement {
  const card = document.createElement('div')
  card.className = 'replay-node-card'

  const title = document.createElement('div')
  title.className = 'replay-node-card__title'
  title.textContent = node.address || '位置'
  card.appendChild(title)

  const sub = document.createElement('div')
  sub.className = 'replay-node-card__sub'
  sub.textContent = buildNodeSubText(node)
  card.appendChild(sub)

  const photos =
    node.type === 'stay' || node.type === 'photo' ? node.photos : undefined
  if (photos && photos.length > 0) {
    const photoCount = node.type === 'stay' ? node.photoCount : photos.length
    const wrap = document.createElement('div')
    wrap.className = 'replay-node-card__photos'
    photos.slice(0, 3).forEach((p) => {
      const span = document.createElement('span')
      span.className = 'replay-node-card__thumb'
      const img = document.createElement('img')
      img.src = p.smallUrl
      img.alt = ''
      span.appendChild(img)
      wrap.appendChild(span)
    })
    if (photoCount && photoCount > 3) {
      const more = document.createElement('span')
      more.className = 'replay-node-card__more'
      more.textContent = `共 ${photoCount} 张`
      wrap.appendChild(more)
    }
    card.appendChild(wrap)
  }

  const footer = document.createElement('div')
  footer.className = 'replay-node-card__footer'
  const prev = document.createElement('span')
  prev.className = `replay-node-card__nav ${index <= 0 ? 'is-disabled' : ''}`
  prev.setAttribute('data-action', 'prev')
  prev.textContent = '‹ 上一个'
  const next = document.createElement('span')
  next.className = `replay-node-card__nav ${
    index >= total - 1 ? 'is-disabled' : ''
  }`
  next.setAttribute('data-action', 'next')
  next.textContent = '下一个 ›'
  footer.appendChild(prev)
  footer.appendChild(next)
  card.appendChild(footer)

  return card
}

/** 节点副标题文案 */
function buildNodeSubText(node: NodeEvent): string {
  if (node.type === 'stay') {
    // 时长缺失/为 0：还在停留中，避免渲染成「NaN分钟」「Invalid date」
    if (!node.stayDurationSec) return '停留中'
    const min = Math.round(node.stayDurationSec / 60)
    const start = dayjs(node.time).format('HH:mm')
    const end = dayjs(node.time + node.stayDurationSec * 1000).format('HH:mm')
    return `停留${min}分钟 (${start}-${end})`
  }
  if (node.type === 'start') {
    return `起点 · ${dayjs(node.time).format('HH:mm')}`
  }
  if (node.type === 'photo') {
    return `拍照 · ${dayjs(node.time).format('HH:mm')}`
  }
  return `当前位置 · ${dayjs(node.time).format('HH:mm')}`
}

export default ReplayMap
